<script setup lang="ts">
import WizardTabWithSidebar from '../WizardTabWithSidebar.vue'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { inject } from 'vue'
import NaturalLegalSelectTab from './NaturalLegalSelectTab.vue'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import WizardHeader from '../WizardHeader.vue'
import { usePersonalAccountFormState } from '../../../utilities/account/useAccountFormPersonal'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { CashIcon } from '@heroicons/vue/solid'
import RiskToleranceForm from './form/RiskToleranceForm.vue'

const { navigateToNextTab, navigateToPreviousTab } = inject('navigation')

const formState = usePersonalAccountFormState()

async function submit() {
    const { valid } = await formState.currentForm.validate()
    if (valid) {
        await formState.submit()
        navigateToNextTab()
    }
}
</script>

<template>
    <WizardTabWithSidebar
        :previous-tab="NaturalLegalSelectTab"
        :next-tab="NaturalLegalSelectTab"
        sidebar-background-image="https://superangels.club/wp-content/uploads/2020/07/ESAC_2020_invest_connect_smart_mobility_72dpi_1600x1200_landscape_BACKGROUND_ONLY.jpg">
        <template #main>
            <WizardHeader :title="$t('account_settings.risk_tolerance')" :icon="CashIcon"></WizardHeader>

            <div class="grid grid-cols-1 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-2 sm:px-6">
                <RiskToleranceForm></RiskToleranceForm>
            </div>
        </template>
        <template #nextButton>
            <BaseButtonPrimary
                :loading="usePersonalAccountDataState().saving"
                :disabled="usePersonalAccountDataState().saving || formState.hasValidationErrors"
                size="lg"
                @click="submit()"
                >{{ $t('widget.save') }}
            </BaseButtonPrimary>
        </template>
        <template #previousButton>
            <BaseButtonMuted size="lg" @click="navigateToPreviousTab()">{{ $t('widget.back') }} </BaseButtonMuted>
        </template>

        <template #sidebar>
            <p class="pb-2 text-base font-bold text-gray-800">Overview of Risk Levels</p>
            <p class="pb-2 text-sm text-gray-800">
                <strong>Low</strong><br />The goal is relatively safe investment results. Exchange rate fluctuations and
                changes in interest rates are possible during the duration of the investment; low risk of capital loss.
            </p>
            <p class="pb-2 text-sm text-gray-800">
                <strong>Medium</strong><br />
                The aim is to achieve relatively safe investment results with a slightly increased risk. Price
                fluctuations and changes in interest rates are possible during the duration of the investment; medium
                risk of capital loss.
            </p>
            <p class="pb-2 text-sm text-gray-800">
                <strong>Advanced</strong><br />
                The aim is to generate higher returns while being aware of the higher risk. Interest rate, currency and
                thus higher exchange rate risks are possible; increased risk of capital loss.
            </p>
            <p class="pb-4 text-sm text-gray-800">
                <strong>High</strong><br />
                The aim is to generate very high yields, aware that they are facing very high risks. High price risks
                and losses from shares, interest rate and currency fluctuations or investments are possible; there is a
                willingness to risk large parts of the investment capital or the entire investment capital (for example,
                total loss in investments).
            </p>
            <p class="pb-2 text-sm text-gray-800">
                EXF Alpha, the syndicate fund and investment vehicle of the European Super Angels Club, is as a Venture
                Capital fund inherently considered a high-risk investment.
            </p>
        </template>
    </WizardTabWithSidebar>
</template>

<style scoped></style>
